import { useEffect, useReducer } from "react";
import { Interval } from "@lifeat/shared/util/TypeUtil";
import { addOnCloseMainWindowEventListener } from "@desktop/ToDestkopEventListeners";
import { useEvent } from "@hooks/useEvent";
import { DebugScope, logDebug } from "@service/ConsoleLoggerService";
import { COUNTDOWN_TIMER_BUFFER_MS } from "../TimerWidgetConstants";

interface CountdownTimerState {
    timer: Interval | null;
    startMillis: number;
    totalMillis: number;
    currMillis: number;
    alarmRang: boolean;
}

type CountdownTimerAction =
    | { type: "start"; timer: Interval }
    | { type: "pause" }
    | { type: "reset"; duration: number; buffer: number; timer: Interval | null }
    | { type: "setAlarmRang"; value: boolean }
    | { type: "setCurrMillis"; value: number };

function countdownTimerReducer(state: CountdownTimerState, action: CountdownTimerAction): CountdownTimerState {
    switch (action.type) {
        case "start":
            return {
                ...state,
                startMillis: Date.now(),
                timer: action.timer,
            };
        case "pause":
            return {
                ...state,
                timer: null,
                totalMillis: state.currMillis,
            };
        case "reset":
            logDebug(DebugScope.Pomodoro, "reset timer", state, action);
            return {
                ...state,
                timer: action.timer,
                startMillis: Date.now(),
                totalMillis: action.duration + action.buffer,
                currMillis: action.duration,
                alarmRang: false,
            };
        case "setAlarmRang":
            return {
                ...state,
                alarmRang: action.value,
            };
        case "setCurrMillis":
            return {
                ...state,
                currMillis: action.value,
            };
    }
}

interface CountdownTimerOption {
    duration: number; // Duration in milliseconds
    onAlarm: () => void;
    alarmRingOffset: number;
    onTimerEnd?: () => void;
}

export const useCountdownTimer = ({ duration, onAlarm, alarmRingOffset, onTimerEnd }: CountdownTimerOption) => {
    const [state, dispatch] = useReducer(countdownTimerReducer, {
        timer: null,
        startMillis: Date.now(),
        totalMillis: duration, // Milliseconds when timer started
        currMillis: duration, // Current milliseconds remaining
        alarmRang: false,
    });

    const tick = useEvent(() => {
        if (!state.timer) {
            return;
        }
        let ms = Math.max(state.totalMillis - Math.round(Date.now() - state.startMillis), 0);

        if (ms <= alarmRingOffset && !state.alarmRang) {
            onAlarm();
            dispatch({ type: "setAlarmRang", value: true });
        }
        if (ms <= 0) {
            clearInterval(state.timer);
            onTimerEnd?.();
            dispatch({ type: "setAlarmRang", value: false });
        } else {
            dispatch({ type: "setCurrMillis", value: ms });
        }
    });

    const start = useEvent(() => {
        // Since a task timer can start while another task timer is running,
        // we clear any existing timer first
        clearInterval(state.timer);
        const timer = setInterval(tick, 100);
        dispatch({ type: "start", timer });
    });

    const pause = useEvent(() => {
        clearInterval(state.timer);
        dispatch({ type: "pause" });
    });

    const reset = useEvent((running: boolean) => {
        clearInterval(state.timer);
        const nextTimer = running ? setInterval(tick, 100) : null;
        dispatch({
            type: "reset",
            duration,
            buffer: COUNTDOWN_TIMER_BUFFER_MS,
            timer: nextTimer,
        });
    });

    useEffect(() => {
        clearInterval(state.timer);
        return () => clearInterval(state.timer);
    }, []);

    // Note: this clears the interval when the desktop app is closed
    // because closing the desktop app does not trigger the useEffect cleanup
    useEffect(() => {
        if (window.todesktop) {
            const unsubscribe = addOnCloseMainWindowEventListener(() => clearInterval(state.timer));

            return () => unsubscribe?.();
        }
        return undefined;
    }, [state.timer]);

    return {
        start,
        pause,
        reset,
        state,
    };
};
